import React, { useState } from 'react';
import { Alert, Paper, TextField, Button, Box, Typography, Link, InputAdornment, IconButton } from '@mui/material';
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { login } from '../services/auth';
import { useNavigate } from 'react-router-dom';
import smartClickLogo from '../images/smartclick-black-min.png';
import Loading from './widgets/Loading';
import PlatformsBackground from './widgets/PlatformsBackground';
import { useTranslation } from 'react-i18next';

function Login() {
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const navigate = useNavigate();

    const handleLogin = async () => {
        if (!email || !password) {
            setErrorMessage(t('login.errorMissingFields'));
            return;
        }
        setErrorMessage('')
        setIsLoading(true)
        try {
            const response = await login(email, password);
            localStorage.setItem('user', JSON.stringify(response.user));
            navigate('/home', { state: { user: response.user } });
        } catch (error) {
            setErrorMessage(t('login.errorLoginFailed'));
        } finally {
            setIsLoading(false)
        }
    };

    const handleTogglePassword = () => { setShowPassword(!showPassword); }

    return (
        <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: "100vh",
            bgcolor: "rgb(242, 242, 242)",
            padding: 2, 
        }}
        >
            <PlatformsBackground/>
            <Paper 
                sx={{
                    textAlign: "center",
                    padding: 4,
                    justifyContent: "center",
                    maxWidth: 400,
                    zIndex: 1,
                }}
                elevation={3}
            >
                {/* Logo */}
                <Box
                    component="img"
                    src={smartClickLogo}
                    alt="Logo"
                    sx={{ marginBottom: 4 }}
                />

                {/* Welcome Message */}
                <Typography variant="h5" sx={{ fontWeight: "bold", marginBottom: 1 }}>
                    {t('login.welcome')}
                </Typography>
                <Typography variant="body1" sx={{ color: "gray", marginBottom: 4 }}>
                    {t('login.instructions')}
                </Typography>

                {/* Email Field */}
                <TextField
                    label={t('login.emailLabel')}
                    placeholder={t('login.emailPlaceholder')}
                    variant="outlined"
                    fullWidth
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    size="small"
                    sx={{ marginBottom: 2, maxWidth: 400 }}
                />

                {/* Password Field */}
                <TextField
                    label={t('login.passwordLabel')}
                    type={showPassword ? "text" : "password"}
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    sx={{ marginBottom: 4, maxWidth: 400 }}
                    InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                        <IconButton onClick={handleTogglePassword} edge="end">
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                        </InputAdornment>
                    ),
                    }}
                />

                {errorMessage && (
                    <Alert severity="error" sx={{ marginBottom: 2 }}>
                        {errorMessage}
                    </Alert>
                )}

                {/* Login Button */}
                <Button
                    variant="contained"
                    color="primary"
                    sx={{ width: "100%", maxWidth: 400, marginBottom: 2 }}
                    onClick={handleLogin}
                >
                    {t('login.loginButton')}
                </Button>

                {/* Links */}
                <Box sx={{ textAlign: "center", marginTop: 2 }}>
                    <Typography variant="body2" sx={{ marginBottom: 1 }}>
                    {t('login.noAccount')} {" "}
                    <Link href="/register" underline="hover">
                        {t('login.registerNow')}
                    </Link>
                    </Typography>
                    <Typography variant="body2">
                    {t('login.forgotPassword')} {" "}
                    <Link href="/reset-password" underline="hover">
                        {t('login.clickHere')}
                    </Link>
                    </Typography>
                </Box>
            </Paper>
            <Loading 
				text={t('login.loading')} 
				isLoading={isLoading} 
			/>
        </Box>
    );
}

export default Login;